/**
 * Store of AI graph-edit diff cards shown in the chat drawer.
 *
 * Model (H2 spec item 2): apply-then-approve. Ops already landed on the live
 * graph; a card is a review affordance, not a gate. Un-actioned cards default
 * to kept. Undo restores the pre-batch snapshot through the SAME history-store
 * accessors Cmd+Z uses — so card-Undo and Cmd+Z agree by construction (one AI
 * batch = one undo step; see recordGraphEdit in tool-executor).
 */
import { create } from 'zustand'
import type { DiffLine } from './edit-diff'
import { useHistoryStore, type HistoryItem } from '@/features/backtest/stores/history-store'

export type DiffCardStatus = 'pending' | 'kept' | 'undone'

export interface DiffCard {
  batchId: string
  /** Short header, e.g. "Strategy edited" or "Strategy built". */
  title: string
  diffLines: DiffLine[]
  status: DiffCardStatus
  timestamp: number
  /**
   * Pre-batch graph snapshot for Undo. This is the exact HistoryItem pushed
   * onto the undo stack for this batch, so restoring it matches Cmd+Z. Absent
   * when no editor history was configured (non-editor pages).
   */
  snapshot?: HistoryItem
}

interface EditDiffState {
  cards: DiffCard[]
  addCard: (card: Omit<DiffCard, 'status' | 'timestamp'>) => void
  keepCard: (batchId: string) => void
  undoCard: (batchId: string) => void
  /** Drop all cards (e.g. new conversation / strategy load). */
  clear: () => void
}

export const useEditDiffStore = create<EditDiffState>((set, get) => ({
  cards: [],

  addCard: (card) =>
    set((s) => ({
      cards: [...s.cards, { ...card, status: 'pending', timestamp: Date.now() }],
    })),

  keepCard: (batchId) =>
    set((s) => ({
      cards: s.cards.map((c) =>
        c.batchId === batchId && c.status === 'pending' ? { ...c, status: 'kept' } : c,
      ),
    })),

  undoCard: (batchId) => {
    const card = get().cards.find((c) => c.batchId === batchId)
    if (!card || card.status === 'undone' || !card.snapshot) return

    const history = useHistoryStore.getState()
    const acc = history.accessors
    if (acc) {
      // If this batch is still the most-recent undo step, delegate to the
      // history store's undo() so the stack stays consistent and Cmd+Z parity
      // is exact. Otherwise restore the captured snapshot directly.
      if (history.past[history.past.length - 1] === card.snapshot) {
        history.undo()
      } else {
        acc.setNodes(card.snapshot.nodes)
        acc.setEdges(card.snapshot.edges)
      }
    }

    set((s) => ({
      cards: s.cards.map((c) => (c.batchId === batchId ? { ...c, status: 'undone' } : c)),
    }))
  },

  clear: () => set({ cards: [] }),
}))

let batchCounter = 0
/** Monotonic id for a single AI edit batch. */
export function nextBatchId(): string {
  return `edit_${++batchCounter}_${Date.now()}`
}
